import React from 'react';
import styles from './ui.module.css';
import useSectionLabel from './useSectionLabel';
import StatGrid, { StatItem } from './StatGrid';
import Pill from './Pill';

export type { StatItem };

interface PageHeaderProps {
  title: React.ReactNode;
  /** Mono caption above the title. Defaults to the nav group the route sits
   *  in ("Masters", "Holdings"…). Pass `null` to suppress it. */
  eyebrow?: React.ReactNode | null;
  /** One line under the title: what this page shows, as-on dates, caveats. */
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** The as-on date of the figures, already formatted (`formatDate`). Shown
   *  as a solid pill ahead of the actions. */
  asOf?: React.ReactNode;
  /** Figures worth knowing before reading the table; up to about five. */
  stats?: StatItem[];
}

/**
 * How every page opens: what it is, and the few figures worth knowing before
 * reading any table. Never put the record count in the title — it lives in the
 * table's footer beside the pager, where it belongs with the paging numbers.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ title, eyebrow, subtitle, actions, asOf, stats }) => {
  const section = useSectionLabel();
  const caption = eyebrow === undefined ? section : eyebrow;
  return (
  <>
    <div className={styles.head}>
      <div>
        {caption && <p className={styles.eyebrow}>{caption}</p>}
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {(actions || asOf) && (
        <div className={styles.headActions}>
          {asOf && (
            <span className={styles.headAsOf}>
              <Pill solid plain>as of {asOf}</Pill>
            </span>
          )}
          {actions}
        </div>
      )}
    </div>

    {stats && <StatGrid items={stats} />}
  </>
  );
};

export default PageHeader;
